import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import { API_URL } from '../Config';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import '../common/commonStyles.css';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const SalesProductReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [inventoryReport, setInventoryReport] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [salescategory, setSalescategory] = useState('');
  const [salesbrand, setSalesbrand] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [footerTotals, setFooterTotals] = useState({
    totalBoxQty: '0',
    totalPiecesQty: '0',
    totalPromoQty: '0',
    totalAmount: '0.00'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const formattedStartDate = format(value[0], 'dd/MM/yyyy');
      const formattedEndDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(formattedStartDate ? formattedStartDate + ' - ' + formattedEndDate : '');
      setPage(0);
    }
  };

  const fetchCategoriesOptions = async () => {
    const apiCategoriesUrl = `${API_URL}/stock_take/getCategories`;
    try {
      const response = await fetch(apiCategoriesUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();

      const formattedOptions = (data.data?.category || []).map(item => ({
        value: item.id,
        label: item.name,
      }));

      setCategoryOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchBrandOptions = async () => {
    const apiBrandsUrl = `${API_URL}/stock_take/getBrands`;
    try {
      const response = await fetch(apiBrandsUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      const formattedOptions = (data.data?.brands || []).map(item => ({
        value: item.id,
        label: item.name,
      }));
      setBrandOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: '',
        reportrange,
        brand_id: salesbrand,
        category_id: salescategory
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/salesProductReport`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setInventoryReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
      }

      if (json) {
        setFooterTotals({
          totalBoxQty: json.BoxQuantity ? parseFloat(json.BoxQuantity.replace(/,/g, '')).toFixed(2) : '0',
          totalPiecesQty: json.PiecesQuantity ? parseFloat(json.PiecesQuantity.replace(/,/g, '')).toFixed(2) : '0',
          totalPromoQty: json.PromoQuantity ? parseFloat(json.PromoQuantity.replace(/,/g, '')).toFixed(2) : '0',
          totalAmount: json.total ? parseFloat(json.total.replace(/,/g, '')).toFixed(2) : '0.00'
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, salesbrand, salescategory]);

  useEffect(() => {
    fetchCategoriesOptions();
    fetchBrandOptions();
  }, []);

  useEffect(() => {
    if (reportrange) {
      fetchReport();
    }
  }, [reportrange, salesbrand, salescategory, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBrandChange = (event, newValue) => {
    setSelectedBrand(newValue);
    setSalesbrand(newValue ? newValue.value : '');
    setPage(0);
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setSalescategory(newValue ? newValue.value : '');
    setPage(0);
  };

  const searchFormData = () => {
    if (dates[0] && dates[1]) {
      const formattedStartDate = format(dates[0], 'dd/MM/yyyy');
      const formattedEndDate = format(dates[1], 'dd/MM/yyyy');
      const newReportRange = formattedStartDate + ' - ' + formattedEndDate;
      setReportrange(newReportRange);
      setPage(0);
    }
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: '1',
      start: '0',
      searchValue: '',
      length: totalCount || 10000,
      reportrange,
      brand_id: salesbrand,
      category_id: salescategory
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const apiUrl = `${config.API_URL}/salesProductReport`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch data for export');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      const exportData = (data.data || []).map((product, index) => ({
        "SR": index + 1,
        "Code": product.product_code || '',
        "Name": product.product_name || '',
        "Category": product.categories || '',
        "Brand": product.brand || '',
        "Total Box Qty": product.BoxQuantity || 0,
        "Total Piece Qty": product.PiecesQuantity || 0,
        "Total Promo Qty": product.PromoQuantity || 0,
        "Total Amount": product.total
          ? parseFloat(product.total.toString().replace(/,/g, '')).toFixed(2)
          : '0.00',
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Product Report');
      XLSX.writeFile(wb, 'Sales_Product_Report.xlsx');
    } catch (error) {
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
              <Typography className="page-title">
                Sales Product Report
              </Typography>
            </Box>
            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  showOneCalendar
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border"
                      {...params}
                      placeholder="Select Category"
                      size="small"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={brandOptions}
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border"
                      {...params}
                      placeholder="Select Brand"
                      size="small"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }} display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={1}>
                <Typography variant="body2" sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212b36", mb: { xs: 1, md: 0 } }}>
                  &nbsp;
                </Typography>
                <Button
                  variant="contained"
                  onClick={searchFormData}
                  className="button-variant-contained"
                  sx={{
         
                    width: { xs: "100%", sm: "100%", md: "auto" },
                  }}
                >
                  Submit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:download-outline" width={20} />}
                  onClick={exportToExcel}
                  className="button-variant-outlined"
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Export to Excel
                </Button>
              </Grid2>
            </Grid2>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer >
                <Table size="medium"
                 className="table-cell-border"
                 sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                      width="50px"
                      >
                        SR
                      </TableCell>
                      <TableCell
                      width="80px"
                      >
                        Code
                      </TableCell>
                      <TableCell
                      width="250px"
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "150px",
                          minWidth: "150px",
                        }}
                      >
                        Category
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "120px",
                          minWidth: "120px",
                        }}
                      >
                        Brand
                      </TableCell>
                      <TableCell
                      width="100px"
                      align="right"
                      >
                        Total Box Qty
                      </TableCell>
                      <TableCell
                      width="120px"
                      align="right"
                      >
                        Total Piece Qty
                      </TableCell>
                      <TableCell
                      width="120px"
                      align="right"
                      >
                        Total Promo Qty
                      </TableCell>
                      <TableCell
                      width="100px"
                      align="right"
                      >
                        Total Amount
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: "#637381" }}>
                            No data available
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      inventoryReport.map((row, index) => (
                        <TableRow
                          key={index}
                          className="table-cell-style"
                          sx={{
                            "&:last-child td": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          <TableCell
                          >
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell
                          >
                            {row.product_code || ""}
                          </TableCell>
                          <TableCell
                          >
                            {row.product_name || ""}
                          </TableCell>
                          <TableCell
                          >
                            {row.categories || ""}
                          </TableCell>
                          <TableCell
                          >
                            {row.brand || ""}
                          </TableCell>
                          <TableCell
                            sx={{
            
                              textAlign: "right",
                   
                            }}
                          >
                            {row.BoxQuantity || "0"}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {row.PiecesQuantity || "0"}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {row.PromoQuantity || "0"}
                          </TableCell>
                          <TableCell
                            sx={{
                            
                              textAlign: "right",
                           
                            }}
                          >
                            {formatNumber(row.total)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="table-cell-style">
                      <TableCell colSpan={4} sx={{ borderBottom: "none" }} />
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "right",
               
                        }}
                      >
                        Total:
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "right",
                    
                        }}
                      >
                        {footerTotals.totalBoxQty}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {footerTotals.totalPiecesQty}
                      </TableCell>
                      <TableCell
                        sx={{
                    
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {footerTotals.totalPromoQty}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          textAlign: "right",
                        }}
                      >
                        {footerTotals.totalAmount}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}

            {!loading && inventoryReport.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
                  className="pagination-styles"
                />
              </Box>
            )}
        </Paper>
      </Box>
    </>
  );
};

export default SalesProductReport;
